import { useEffect, useMemo, useState } from "react";
import { Box, Stack, Divider, Typography, Avatar, useTheme, MenuItem, alpha, CircularProgress } from "@mui/material";
import { GridColDef, GridRowParams } from "@mui/x-data-grid";
import { Button, ConfirmationDialog, IconButton, Menu, TooltipShortcut } from "@itk/mui-components";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { createGuid, hasValue, hasValueAndNotEmpty, Nullable } from "@itk/typescript-utils";
import { connect } from "react-redux";
import { userManagementActions } from "./user-management-store";
import { RootState } from "src/store";
import { AddEditUserDialog } from "./components/add-edit-user-dialog/add-edit-user-dialog";
import { ContactPersonExtendedGet, SearchState } from "./user-management-types";
import { useForm } from "@itk/react-form";
import { Search } from "./components/search/search";
import { DataGridWithIdentity, getContactPersonPermissions, usePermissionsTranslation } from "src/components";
import { DekoxIcon } from "src/components/deko-x-icons";
import { informationPageUsersSectionK, useInformationPageUsersSectionT } from "src/translation-hooks/information-page-users-section";
import { genericTranslationsK, useGenericTranslationsT } from "src/translation-hooks/generic-translations";
import { Shared_ContactPersonPermissionKinds } from "src/backend/openapi/v1";
import { useGetCustomerId, usePermissionCheck } from "src/hooks";
import { applicationActions } from "src/components/application-store";
import { IDENTITY_SETTINGS_KEYS } from "src/utils/types/identity-settings-types";

const mapStateToProps = (state: RootState) => ({
  contactPersonsLoading: state.pageReducer.informationPageReducer.userMananementReducer.contactPersonsLoading,
  addEditDialogProps: state.pageReducer.informationPageReducer.userMananementReducer.addEditDialogProps,
  listShouldReload: state.pageReducer.informationPageReducer.userMananementReducer.listShouldReload,
  identitySettings: state.generalReducer.applicationReducer.identitySettings
});

const ownActions = {
  setAddEditDialogProps: userManagementActions.setAddEditDialogProps,
  getContactPersonsRequest: userManagementActions.getContactPersonsRequest,
  clear: userManagementActions.clear,
  setListShouldReload: userManagementActions.setListShouldReload,
  deleteContactPersonRequest: userManagementActions.deleteContactPersonRequest,
  setIdentitySettingsRequest: applicationActions.setIdentitySettingsRequest
};

type Props = ReturnType<typeof mapStateToProps> & typeof ownActions;

const UserManagementBase = (props: Props) => {
  const informationPageUsersSectionT = useInformationPageUsersSectionT();
  const genericTranslationsT = useGenericTranslationsT();

  const {
    setAddEditDialogProps,
    addEditDialogProps,
    contactPersonsLoading,
    getContactPersonsRequest,
    clear,
    setListShouldReload,
    listShouldReload,
    deleteContactPersonRequest,
    identitySettings,
    setIdentitySettingsRequest
  } = props;
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const { customerId } = useGetCustomerId();
  const [isAllowed] = usePermissionCheck([Shared_ContactPersonPermissionKinds.ManageOtherPublicUser]);

  useEffect(() => {
    return () => {
      clear();
    };
  }, []);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    if (customerId && contactPersonsLoading.loading === "not-started") {
      getContactPersonsRequest(customerId);
    }
  }, [contactPersonsLoading, customerId]);

  useEffect(() => {
    if (customerId && listShouldReload) {
      getContactPersonsRequest(customerId);
      setListShouldReload(false);
    }
  }, [listShouldReload, getContactPersonsRequest, setListShouldReload, customerId]);

  const [showConfirmation, setShowConfirmation] = useState(false);
  const [selectedContactPersonId, setSelectedContactPersonId] = useState<string | undefined>(undefined);
  const handleOnConfirm = () => {
    setShowConfirmation(false);

    if (hasValue(selectedContactPersonId)) {
      deleteContactPersonRequest(selectedContactPersonId);
      setSelectedContactPersonId(undefined);
    }
  };

  const { getPermissionTranslation } = usePermissionsTranslation();

  const initialValues = useMemo((): Nullable<SearchState> => {
    return {
      wildcardSearch: "",
      permission: undefined
    };
  }, []);

  const formSystem = useForm<SearchState>(initialValues);

  const { getValues } = formSystem;

  const filteredData = useMemo(() => {
    if (contactPersonsLoading.loading === "has-loaded") {
      const values = getValues();
      if (hasValue(values)) {
        if (hasValueAndNotEmpty(values.wildcardSearch)) {
          return contactPersonsLoading.data.filter(item => {
            return values.wildcardSearch && (item.fullName.toLowerCase().includes(values.wildcardSearch.toLowerCase()) || item.email.toLowerCase().includes(values.wildcardSearch.toLowerCase()));
          });
        }
        if (hasValueAndNotEmpty(values.permission)) {
          return contactPersonsLoading.data.filter(item => {
            return values.permission && item.permissions.includes(values.permission);
          });
        }
      }
      return contactPersonsLoading.data;
    }
    return [];
  }, [contactPersonsLoading, getValues]);

  const columns: GridColDef<ContactPersonExtendedGet>[] = useMemo(() => {
    const defaultColumns: GridColDef<ContactPersonExtendedGet>[] = [
      {
        field: "name",
        headerName: informationPageUsersSectionT.users,
        flex: 1,
        renderCell: params => {
          const name = params?.row?.fullName ?? "";
          const initials = name
            .split(" ")
            .map((n: string) => n[0])
            .join("")
            .slice(0, 2)
            .toUpperCase();

          return (
            <Stack direction="row" alignItems="center" spacing={1}>
              <Avatar sx={{ background: alpha(theme.palette.customColors.darkGrey, 0.5), width: 32, height: 32, fontSize: 14 }}>{initials}</Avatar>
              <Typography variant="subtitle1">{name}</Typography>
            </Stack>
          );
        }
      },

      { field: "email", headerName: genericTranslationsT.email, flex: 1 },
      {
        field: "access",
        headerName: informationPageUsersSectionT.extendedAccess,
        flex: 2,
        renderCell: params =>
          params.row.permissions.length > 0 ? (
            <Stack overflow="hidden" direction="row" spacing={1} divider={<Divider orientation="vertical" flexItem />}>
              {Object.values(getContactPersonPermissions())
                .filter(x => params.row.permissions.includes(x))
                .map(item => (
                  <Typography key={item} variant="subtitle1" textOverflow="ellipsis" whiteSpace="nowrap">
                    {getPermissionTranslation(item)}
                  </Typography>
                ))}
            </Stack>
          ) : null
      }
    ];
    if (isAllowed) {
      defaultColumns.push({
        field: "actions",
        headerName: "",
        width: 50,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: params => (
          <>
            <Stack direction="row" justifyContent="flex-end" width="100%">
              <IconButton
                size="small"
                k={genericTranslationsK.more}
                onClick={event => {
                  setSelectedContactPersonId(params.row.contactPersonId);
                  handleMenuOpen(event);
                }}
              >
                <MoreVertIcon />
              </IconButton>
            </Stack>
            <Menu
              anchorEl={anchorEl}
              open={hasValue(anchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              sx={{ "& .MuiPaper-root": { boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)" } }}
            >
              <MenuItem sx={{ gap: 1 }} onClick={() => hasValue(selectedContactPersonId) && setAddEditDialogProps({ variant: "edit", selectedContactPersonId })}>
                <DekoxIcon icon="edit" sx={{ mr: 0.62 }} />
                <Typography>{informationPageUsersSectionT.editUser}</Typography>
              </MenuItem>
              <MenuItem sx={{ gap: 1 }}>
                <BlockOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
                {informationPageUsersSectionT.markAsInactive}
              </MenuItem>
              <MenuItem sx={{ gap: 1 }} onClick={() => setShowConfirmation(true)}>
                <DeleteOutlineOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
                <Typography>{informationPageUsersSectionT.deleteUser}</Typography>
              </MenuItem>
            </Menu>
          </>
        )
      });
    }
    return defaultColumns;
  }, [informationPageUsersSectionT, genericTranslationsK, anchorEl, isAllowed, selectedContactPersonId]);

  return (
    <Stack flex={1} overflow="auto" paddingY={2}>
      <Stack direction="row" gap={2} mb={2} justifyContent="space-between" alignItems="center">
        <Search formValues={formSystem} />
        {isAllowed && (
          <Box>
            <TooltipShortcut shortcut={{ action: () => setAddEditDialogProps({ variant: "create", selectedContactPersonId: createGuid() }), shortCutKey: "Insert" }}>
              <Button
                t={informationPageUsersSectionT}
                field="newUser"
                k={informationPageUsersSectionK}
                size="medium"
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setAddEditDialogProps({ variant: "create", selectedContactPersonId: createGuid() })}
              />
            </TooltipShortcut>
          </Box>
        )}
      </Stack>
      <Box flex={1} overflow="auto">
        {contactPersonsLoading.loading === "is-loading" ? (
          <Stack height="100%" alignItems="center" justifyContent="center">
            <CircularProgress />
          </Stack>
        ) : (
          <DataGridWithIdentity
            dataGridSettings={identitySettings[IDENTITY_SETTINGS_KEYS.USER_MANAGEMENT_DATA_GRID]}
            handleSaveSettings={value => {
              setIdentitySettingsRequest(IDENTITY_SETTINGS_KEYS.USER_MANAGEMENT_DATA_GRID, JSON.stringify(value));
            }}
            rows={filteredData}
            columns={columns}
            getRowId={row => row.contactPersonId}
            disableColumnMenu
            hideFooter
            onRowDoubleClick={(params: GridRowParams) => {
              if (isAllowed) hasValue(params.row.contactPersonId) && setAddEditDialogProps({ variant: "edit", selectedContactPersonId: params.row.contactPersonId });
            }}
            onCellKeyDown={(rowEvent, e) => {
              if (e.key === "Enter" && rowEvent.colDef.field !== "actions") {
                if (isAllowed) hasValue(rowEvent.row.contactPersonId) && setAddEditDialogProps({ variant: "edit", selectedContactPersonId: rowEvent.row.contactPersonId });
              }
            }}
            sx={{
              fontWeight: "normal",
              color: theme.palette.customColors.darkGrey,
              background: theme.palette.customColors.white,
              borderRadius: 1,
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: theme.typography.fontWeightBold,
                color: theme.palette.customColors.darkGrey,
                fontSize: theme.typography.body2.fontSize
              },
              "& .MuiDataGrid-virtualScroller": {
                overflowX: "hidden",
                fontSize: theme.typography.subtitle1.fontSize
              }
            }}
          />
        )}
      </Box>
      {hasValue(addEditDialogProps) && <AddEditUserDialog addEditDialogInfo={addEditDialogProps} />}
      {showConfirmation && (
        <ConfirmationDialog
          message={informationPageUsersSectionT.deleteConfirmationMessage}
          onConfirm={handleOnConfirm}
          onCancel={() => {
            setSelectedContactPersonId(undefined), setShowConfirmation(false);
          }}
        />
      )}
    </Stack>
  );
};

export const UserManagement = connect(mapStateToProps, ownActions)(UserManagementBase);
